import { useCallback, useEffect } from 'react';
import {
  useVideoAnalytics,
  type VideoPlaybackRestore,
} from '@libertify.frontend/analytics';
import type Player from 'video.js/dist/types/player';

/**
 * Segment analytics for the Knowledge Hub Video add-on's
 * `ChapteredVideoPlayer`.
 *
 * Reuses `useVideoAnalytics` — the same `media_player_*` family (impression,
 * play/resume, pause, seek, ended, heartbeat, playback-rate change) and
 * watch/skip-time bookkeeping the legacy `/app` player emits — so dashboards
 * union both surfaces. Every event additionally carries
 * `player_surface: 'addon'` + `addon: 'video'` to keep the add-on player
 * separable from the main viewer.
 *
 * Pass the returned `onPlayerReady` as the player's `onReady`. The player
 * only mounts when the viewer expands the poster card, so the impression it
 * fires on setup is the poster click. Chapters events (`media_player_chapter*`)
 * are not handled here — `ChaptersOverlayComponent` already emits them.
 */
export function useAddonVideoAnalytics(props?: {
  projectId?: string;
  publicationId?: string;
  organizationId?: string;
  workspaceId?: string;
}) {
  const { setupPlayerTracking, handleUnload, resetTracking } =
    useVideoAnalytics({
      projectId: props?.projectId,
      publicationId: props?.publicationId,
      organizationId: props?.organizationId,
      workspaceId: props?.workspaceId,
      playerSurface: 'addon',
      addon: 'video',
    });

  // Each `onReady` is a brand-new player starting from 0:00 — this hook
  // outlives the player (it's page-level; the player mounts per poster
  // expand). Reset the tracker's session state (hasStarted, watch/skip time)
  // so the first play of a re-expanded player emits `media_player_play`
  // again instead of `media_player_resumed` with inherited watch_time.
  // `restore`: saved position/speed just set on the player (see
  // useVideoProgress); its seek and rate change aren't tracked.
  const onPlayerReady = useCallback(
    (player: Player, restore?: VideoPlaybackRestore) => {
      resetTracking();
      setupPlayerTracking(player, restore);
    },
    [resetTracking, setupPlayerTracking],
  );

  // Final heartbeat when the viewer leaves mid-playback. Both events are
  // bound because pagehide fires on mobile Safari where beforeunload doesn't
  // — but on a normal reload BOTH fire (beforeunload, then pagehide) and
  // handleUnload doesn't mark the session stopped, so an unguarded pair
  // reports the final heartbeat twice. Fire once per hide episode and re-arm
  // on pageshow for a page revived from the bfcache.
  useEffect(() => {
    let reported = false;
    const reportOnce = () => {
      if (reported) return;
      reported = true;
      handleUnload();
    };
    const rearm = () => {
      reported = false;
    };
    window.addEventListener('pagehide', reportOnce);
    window.addEventListener('beforeunload', reportOnce);
    window.addEventListener('pageshow', rearm);
    return () => {
      window.removeEventListener('pagehide', reportOnce);
      window.removeEventListener('beforeunload', reportOnce);
      window.removeEventListener('pageshow', rearm);
    };
  }, [handleUnload]);

  return {
    /** Wire as `<ChapteredVideoPlayer onReady={onPlayerReady} />`. */
    onPlayerReady,
  };
}
